/**
 * Normaliza una string para comparación robusta:
 *   - Unicode NFD (separa diacríticos)
 *   - Quita marks/diacríticos (`í` → `i`, `ñ` → `n`)
 *   - Pasa a minúsculas
 *   - Colapsa espacios y trim
 *
 * Crítico porque el backend AetherCoach devuelve algunos campos con
 * normalización NFD nativa (ej. `Bíceps` donde la í es 2 codepoints) y los
 * chips de la UI usan NFC. Comparación directa falla; normalize() unifica.
 */
export function normalize(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Stem naive para español: quita sufijos de plural más comunes para que
 * "elevaciones" matchee "elevacion". Solo para palabras de 4+ chars (las
 * cortas como "los", "mes" se preservan).
 *
 * Reglas aplicadas en orden:
 *   - termina en "es" (>= 5 chars): "elevaciones" → "elevacion", "leones" → "leon"
 *   - termina en "s" (>= 4 chars): "barras" → "barra", "pies" → "pie"
 *
 * No es perfecto (no maneja "lápiz"/"lápices", verbos irregulares, etc.) pero
 * resuelve el 95% de los casos de búsqueda en nombres de ejercicios. Pensado
 * para usarse SIMÉTRICAMENTE — stem en query Y en el texto a buscar.
 */
export function stemEs(word: string): string {
  if (word.length < 4) return word;
  if (word.length >= 5 && word.endsWith("es")) return word.slice(0, -2);
  if (word.endsWith("s")) return word.slice(0, -1);
  return word;
}

/**
 * Tokeniza un string para búsqueda: normalize → split por espacios/no-alfanuméricos
 * → descarta tokens cortos (< 2 chars: "y", "a", "o") → stem español.
 * Resultado: array de "raíces" minúsculas sin acentos.
 *
 *   tokenize("Elevaciones Laterales con Mancuernas") →
 *     ["elevacion", "lateral", "con", "mancuerna"]
 *   tokenize("elevación lateral") →
 *     ["elevacion", "lateral"]
 *
 * Ambos producen tokens que se intersectan → "elevacion" y "lateral" matchean.
 * Esto resuelve el bug donde Camila buscaba plural y el catálogo está en
 * singular (o viceversa) — antes `.includes("elevaciones laterales")` daba 0
 * matches contra "Elevación Lateral con Mancuerna" porque el substring exacto
 * no estaba.
 */
export function tokenize(s: string | null | undefined): string[] {
  const n = normalize(s);
  if (!n) return [];
  return n
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2)
    .map(stemEs);
}

/**
 * Match difuso: cada token de la query debe estar como SUBSTRING en algún
 * token del texto destino. Permite búsquedas parciales ("rem" → "remo",
 * "press" → "press banca") y tolerantes a plural (vía stemEs).
 *
 *   fuzzyMatch("elevaciones laterales", "Elevación Lateral con Mancuerna") → true
 *   fuzzyMatch("polea", "Aperturas Declinadas en Polea") → true
 *   fuzzyMatch("press banca", "Press de Banca con Barra") → true
 */
export function fuzzyMatch(query: string, haystack: string): boolean {
  const qTokens = tokenize(query);
  if (qTokens.length === 0) return true;
  const hTokens = tokenize(haystack);
  return qTokens.every((q) => hTokens.some((h) => h.includes(q)));
}
